import React, { useState, useEffect } from 'react'
import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Box,
  Card,
  useTheme,
} from '@mui/material'

import Arrow from 'atoms/Arrow'
import Title from '../title/Title'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const TableList = ({
  data,
  selected,
  onSelectionChange,
  lifecycleFundBreakdowns,
  initialAllocation,
  redact = false,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState([])
  const [hoveredRow, setHoveredRow] = useState(null)

  const formatPercent = (p) =>
    redact ? 'XX %' : `${Number(p ?? 0).toFixed(2)}%`
  const formatAmount = (v) =>
    redact ? '$XX,XXX' : dollarFormat.format(Number(v ?? 0))

  useEffect(() => {
    if (selected?.dataIndex !== undefined) {
      setOpen([selected.dataIndex])
    } else {
      setOpen([])
    }
  }, [selected])

  const handleRowClick = (index) => {
    const willOpen = !open.includes(index)
    const newOpen = willOpen ? [index] : []
    setOpen(newOpen)
    onSelectionChange(
      willOpen ? { type: 'pie', seriesId: 'A', dataIndex: index } : null
    )
  }

  const isActive = (index) => open.includes(index)

  const getRowColor = (index) => {
    if (hoveredRow === index) return isActive(index) ? '#f1f1f1' : '#f9f9f9'
    return isActive(index) ? '#f1f1f1' : 'transparent'
  }

  const fundDetailsMap = {
    tspGFund: {
      label: 'G Fund',
      examples: 'U.S. Treasury securities',
      description:
        'Invests exclusively in short-term U.S. Treasury securities. Offers stable returns and protection against loss of principal, with interest rates similar to long-term government bonds.',
    },
    tspFFund: {
      label: 'F Fund',
      examples: 'Government, corporate, and mortgage-backed bonds',
      description:
        'Tracks the Bloomberg U.S. Aggregate Bond Index, covering a broad range of the U.S. bond market. As of December 2024, the portfolio includes roughly 48% government-related bonds, 24% credit, and 27% securitized debt. Provides income with moderate risk and lower volatility than stock funds.',
    },
    tspCFund: {
      label: 'C Fund',
      examples: 'Apple, Nvidia, Microsoft, Amazon, Meta, Tesla',
      benchmark: 'S&P 500',

      description:
        'Provides exposure to large and mid-sized U.S. companies by tracking the S&P 500 Index. Investment in the C Fund offers the opportunity to participate in the growth of major corporations such as Apple, Nvidia, Microsoft, and Amazon.',
    },
    tspSFund: {
      label: 'S Fund',
      examples: 'Doordash, Coinbase, Snowflake, Block, Trade Desk',
      description:
        'Tracks the Dow Jones U.S. Completion Total Stock Market Index, which includes small- to mid-sized U.S. companies not found in the S&P 500. Offers high growth potential but with greater volatility.',
    },
    tspIFund: {
      label: 'I Fund',
      examples: 'Taiwan Semiconductor, ASML, Nestlé, Toyota, AstraZeneca',

      description:
        'Provides international diversification by investing in companies from more than 40 developed and emerging market countries (excluding the U.S.). Tracks a global index and includes large, medium, and small non-U.S. companies.',
    },

    tspLIncome: {
      label: 'L Income',
      examples: 'G, F, C, S, I',
      description:
        'Designed for participants who are withdrawing or about to begin withdrawing their TSP accounts. Offers low risk and prioritizes capital preservation and income.',
    },
    tspL2025: {
      label: 'L 2025',
      examples: 'G, F, C, S, I',
      description:
        'For participants born between 1960–1964 or planning to begin withdrawals between 2025–2027. Offers a low to medium risk profile with a gradually shifting asset mix.',
    },
    tspL2030: {
      label: 'L 2030',
      examples: 'G, F, C, S, I',
      description:
        'For participants born between 1965–1969 or planning to begin withdrawals between 2028–2032. Offers a medium risk profile with automatic quarterly adjustments.',
    },
    tspL2035: {
      label: 'L 2035',
      examples: 'G, F, C, S, I',
      description:
        'For participants planning to retire in the early-to-mid 2030s. Offers a moderate risk level and shifts gradually to preserve capital.',
    },
    tspL2040: {
      label: 'L 2040',
      examples: 'G, F, C, S, I',
      description:
        'For participants retiring around 2040. Designed for growth with a moderate to high risk profile that reduces over time.',
    },
    tspL2045: {
      label: 'L 2045',
      examples: 'G, F, C, S, I',
      description:
        'For participants targeting retirement between 2043–2047. Offers long-term growth with higher risk and daily rebalancing.',
    },
    tspL2050: {
      label: 'L 2050',
      examples: 'G, F, C, S, I',
      description:
        'For participants born between 1985–1989 or planning to begin withdrawals between 2048–2052. Designed for long-term growth with higher risk early on.',
    },
    tspL2055: {
      label: 'L 2055',
      examples: 'G, F, C, S, I',
      description:
        'For younger participants born after 1990 with a long investment horizon. High-growth, high-risk approach that becomes more conservative over time.',
    },
    tspL2060: {
      label: 'L 2060',
      examples: 'G, F, C, S, I',
      description:
        'Designed for those planning to withdraw funds in the early 2060s. Offers aggressive growth and long-term capital appreciation.',
    },
    tspL2065: {
      label: 'L 2065',
      examples: 'G, F, C, S, I',
      description:
        'For participants with a very long time horizon, such as those retiring after 2063. High-growth allocation with frequent rebalancing.',
    },
    tspL2070: {
      label: 'L 2070',
      examples: 'G, F, C, S, I',
      description:
        'Ideal for participants in their early careers or born after 2000. Maximizes growth potential with high equity exposure early on.',
    },
    tspL2075: {
      label: 'L 2075',
      examples: 'G, F, C, S, I',
      description:
        'Designed for the youngest investors or future generations. Targets maximum long-term growth with a glide path to safety.',
    },
  }

  return (
    <TableContainer
      sx={{
        margin: 'auto',
        width: '100%',
        borderRadius: 0,
        marginTop: redact ? 0 : 4,
        backgroundColor: '#f9f9f9',
        borderBottomLeftRadius: '20px',
        borderBottomRightRadius: '20px',
      }}
    >
      <Table sx={{ width: '100%' }} aria-label="collapsible table">
        <TableBody>
          {/* Header */}
          <TableRow>
            <TableCell colSpan={1} sx={{ px: 0 }}>
              <Typography
                variant="body1Medium"
                sx={{ pl: { xs: 3, md: 4 }, color: '#A3A7AA' }}
              >
                Funds
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ px: 0 }}>
              <Typography
                variant="body1Medium"
                sx={{
                  color: '#A3A7AA',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                Amount %
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ px: 0 }}>
              <Typography
                variant="body1Medium"
                sx={{
                  color: '#A3A7AA',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                $ Amount
              </Typography>
            </TableCell>
            <TableCell sx={{ p: 0, px: 0, width: '0%' }} />
          </TableRow>

          {/* Rows */}
          {data.map((row, index) => (
            <React.Fragment key={row.id ?? index}>
              <TableRow
                onClick={() => handleRowClick(index)}
                onMouseEnter={() => setHoveredRow(index)}
                onMouseLeave={() => setHoveredRow(null)}
                sx={{
                  backgroundColor: getRowColor(index),
                  transition: 'background-color 0.2s ease-in-out',
                  '&:hover': { cursor: 'pointer' },
                }}
              >
                <TableCell sx={{ pl: { xs: 2, md: 4 }, borderBottom: 0, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Title highlighted={index} data={data} />
                  </Box>
                </TableCell>

                <TableCell sx={{ borderBottom: 0, p: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: theme.typography.body2.fontWeight,
                      color: '#495057',
                      textAlign: 'right',
                    }}
                  >
                    {formatPercent(row.percent)}
                  </Typography>
                </TableCell>

                <TableCell sx={{ borderBottom: 0, p: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: theme.typography.body2.fontWeight,
                      color: '#495057',
                      textAlign: 'right',
                    }}
                  >
                    {formatAmount(row.value)}
                  </Typography>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    borderBottom: 0,
                    px: { xs: 1, md: 4 },
                    textAlign: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Arrow
                    color={
                      isActive(index)
                        ? 'gray'
                        : hoveredRow === index
                        ? 'gray'
                        : 'grayInactive'
                    }
                    direction="down"
                    hovered={hoveredRow === index}
                    isOpen={isActive(index)}
                  />
                </TableCell>
              </TableRow>

              {/* Expandable Row */}
              <TableRow sx={{ pb: 1 }}>
                <TableCell
                  colSpan={4}
                  sx={{
                    py: 0,
                    px: { xs: 2, md: 5 },
                    backgroundColor: getRowColor(index),
                  }}
                >
                  <Collapse in={isActive(index)} timeout="auto" unmountOnExit>
                    <Card
                      sx={{
                        my: 0,
                        border: '2px solid #A3A7AA',
                        borderRadius: 4,
                      }}
                    >
                      <Table sx={{ width: '100%' }}>
                        <TableBody
                          sx={{ backgroundColor: theme.palette.common.white }}
                        >
                          <TableRow>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 600,
                                  color: '#A3A7AA',
                                }}
                              >
                                Risk Level
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 500,
                                  color: '#495057',
                                }}
                              >
                                {row.risk}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell sx={{ pr: { xs: 0, lg: 1 } }}>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 600,
                                  color: '#A3A7AA',
                                }}
                              >
                                Benchmark
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ pr: { xs: 0, lg: 1 } }}>
                              {lifecycleFundBreakdowns[row.key] ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                  }}
                                >
                                  {Object.entries(
                                    lifecycleFundBreakdowns[row.key]
                                  ).map(([fundKey, percent]) => {
                                    const fundData = initialAllocation[fundKey]
                                    if (!fundData) return null
                                    return (
                                      <Box
                                        key={fundKey}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Title
                                          highlighted={0}
                                          data={[
                                            {
                                              title: fundData.label,
                                              color: fundData.color,
                                            },
                                          ]}
                                        />
                                        <Typography
                                          sx={{
                                            ml: 0.5,
                                            mr: 0,
                                            fontSize: 14,
                                            color: '#495057',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {redact
                                            ? 'XX %'
                                            : `${percent.toFixed(2)}%`}
                                        </Typography>
                                      </Box>
                                    )
                                  })}
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    color: '#495057',
                                  }}
                                >
                                  {fundDetailsMap[row.key]?.benchmark || '—'}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell sx={{ pr: { xs: 0, lg: 1 } }}>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 600,
                                  color: '#A3A7AA',
                                }}
                              >
                                Examples
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ pr: { xs: 0, lg: 1 } }}>
                              {lifecycleFundBreakdowns[row.key] ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                  }}
                                >
                                  {Object.entries(
                                    lifecycleFundBreakdowns[row.key]
                                  ).map(([fundKey, percent]) => {
                                    const fundData = initialAllocation[fundKey]
                                    if (!fundData) return null
                                    return (
                                      <Box
                                        key={fundKey}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Title
                                          highlighted={0}
                                          data={[
                                            {
                                              title: fundData.label,
                                              color: fundData.color,
                                            },
                                          ]}
                                        />
                                        <Typography
                                          sx={{
                                            ml: 0.5,
                                            mr: 0,
                                            fontSize: 14,
                                            color: '#495057',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {redact
                                            ? 'XX %'
                                            : `${percent.toFixed(2)}%`}
                                        </Typography>
                                      </Box>
                                    )
                                  })}
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    color: '#495057',
                                  }}
                                >
                                  {fundDetailsMap[row.key]?.examples || '—'}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              sx={{
                                pr: { xs: 0, lg: 1 },
                                textAlign: 'top',
                                verticalAlign: 'top',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 600,
                                  color: '#A3A7AA',
                                }}
                              >
                                Description
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 500,
                                  color: '#495057',
                                }}
                              >
                                {fundDetailsMap[row.key]?.description || '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Card>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default TableList
